import { Spin, Typography, Tooltip } from "antd"
import { CloseOutlined, MinusOutlined } from "@ant-design/icons"
import React from "react"
import axiosInstance from "../axios/axiosConfig"
import { doc, getDoc } from "firebase/firestore"
import { db } from "../firebase/config" // adjust path as needed

const { Text } = Typography

// Caches
const knifeCache = new Map()
const materialCache = new Map()

// Machine startup constant
const MACHINE_STARTUP_METERS = 20

async function getKnife(knifeCode) {
  const code = String(knifeCode || "").trim()
  if (!code || code.toLowerCase() === "none") return null
  if (knifeCache.has(code)) return knifeCache.get(code)

  try {
    const res = await axiosInstance.get(`/api/searchByCode?code=${encodeURIComponent(code)}`)
    const knife = res.data || null
    knifeCache.set(code, knife)
    return knife
  } catch (err) {
    console.error("Knife fetch error:", err)
    knifeCache.set(code, null)
    return null
  }
}

async function getMaterial(materialId) {
  const id = String(materialId || "").trim()
  if (!id) return null
  if (materialCache.has(id)) return materialCache.get(id)

  try {
    const docRef = doc(db, "materials", id)
    const docSnap = await getDoc(docRef)
    const material = docSnap.exists() ? { ...docSnap.data(), id: docSnap.id } : null
    materialCache.set(id, material)
    return material
  } catch (err) {
    console.error("Material fetch error:", err)
    materialCache.set(id, null)
    return null
  }
}

function calcProjectedMeters(order, knife) {
  const labelCount = Number(order?.amountCreated) || 0
  if (!knife || labelCount <= 0) return 0

  const height = Number(knife?.height) || 0
  const gapRaport = Number(knife?.gapRaport) || 0
  const rows = Number(knife?.rowss) || 0
  if (rows <= 0) return 0

  const a1 = (height + gapRaport) * 0.001
  const a2 = Number((1000 / rows).toFixed(2))
  const a3 = Math.ceil(a1 * a2)
  const countInThousands = labelCount / 1000
  const projected = a3 * countInThousands

  return Number.isFinite(projected) && projected > 0 ? projected : 0
}

function calcDeltaM2(order, deltaMeters) {
  const widthMm =
    Number(order?.selectedProduct?.formatWidth) || Number(order?.selectedProduct?.labelWidth) || 0
  const widthM = widthMm / 1000
  return widthM ? deltaMeters * widthM : 0
}

export function MaterialVarianceValue({ order }) {
  const [state, setState] = React.useState({
    loading: true,
    error: null,
    projectedBase: 0,
    projectedTotal: 0,
    used: 0,
    deltaM: 0,
    deltaM2: 0,
    costDelta: 0,
    eurM2: 0,
  })

  React.useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const used = Number(order?.machineCompletion?.usedMeters ?? order?.usedMeters ?? 0) || 0

        let projectedBase = Number(order?.projectedMeters ?? 0) || 0

        if (!projectedBase) {
          const knifeCode = order?.selectedProduct?.knifeCode
          const knife = await getKnife(knifeCode)
          projectedBase = calcProjectedMeters(order, knife)
        }

        // Add machine startup meters
        const projectedTotal = projectedBase + MACHINE_STARTUP_METERS

        const deltaM = projectedTotal - used
        const deltaM2 = calcDeltaM2(order, deltaM)

        // Fetch material to get cost
        const materialId = order?.selectedProduct?.materialId
        const material = await getMaterial(materialId)
        const eurM2 = Number(material?.eurM2) || 0
        const costDelta = Math.abs(deltaM2) * eurM2

        if (!alive) return
        setState({
          loading: false,
          error: null,
          projectedBase,
          projectedTotal,
          used,
          deltaM,
          deltaM2,
          costDelta,
          eurM2,
        })
      } catch (e) {
        if (!alive) return
        setState((s) => ({ ...s, loading: false, error: e?.message ?? "Error" }))
      }
    })()
    return () => {
      alive = false
    }
  }, [order])

  // If the order was split, we don't calculate variance
  if (order?.splitOrder) {
    return (
      <Tooltip title="Split order — variance not calculated">
        <MinusOutlined />
      </Tooltip>
    )
  }

  if (state.loading) return <Spin size="small" />
  if (state.error) return <Text type="danger">ERR</Text>

  const { projectedBase, projectedTotal, used, deltaM, deltaM2, costDelta, eurM2 } = state
  const hasAny = Number(projectedTotal) || Number(used)
  if (!hasAny) return <CloseOutlined />

  const color = deltaM >= 0 ? "green" : "red"
  const sign = deltaM > 0 ? "+" : ""
  const costSign = deltaM > 0 ? "+" : "-"

  return (
    <Tooltip
      title={
        <div style={{ fontSize: "12px" }}>
          <div>
            <strong>Projected:</strong> {Math.round(projectedBase)} m{" "}
            <span style={{ opacity: 0.7 }}>(+{MACHINE_STARTUP_METERS}m startup)</span>
          </div>
          <div>
            <strong>Total Projected:</strong> {Math.round(projectedTotal)} m
          </div>
          <div>
            <strong>Used:</strong> {Math.round(used)} m
          </div>
          <div style={{ marginTop: 4 }}>
            <strong>Difference:</strong>{" "}
            <span style={{ color: deltaM >= 0 ? "#52c41a" : "#ff4d4f" }}>
              {sign}
              {Math.round(deltaM)} m
            </span>
          </div>
          <div>
            <strong>Area Difference:</strong>{" "}
            <span style={{ color: deltaM >= 0 ? "#52c41a" : "#ff4d4f" }}>
              {sign}
              {Math.abs(deltaM2).toFixed(2)} m²
            </span>
          </div>
          {eurM2 > 0 && (
            <div
              style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid rgba(255,255,255,0.2)" }}>
              <div style={{ fontSize: "11px", opacity: 0.8 }}>Material: €{eurM2.toFixed(2)}/m²</div>
            </div>
          )}
        </div>
      }>
      <div style={{ lineHeight: 1.2 }}>
        <span style={{ color, fontSize: "15px" }}>
          {costSign}€{costDelta.toFixed(2)}
        </span>
      </div>
    </Tooltip>
  )
}

export async function computeMaterialVarianceForExport(order) {
  // mirror the component’s guards/logic so results match exactly
  if (order?.splitOrder) return { kind: "skip", reason: "split" }
  if (order?.selectedProduct?.machine === "Tape") return { kind: "skip", reason: "tape" }

  const used = Number(order?.machineCompletion?.usedMeters ?? order?.usedMeters ?? 0) || 0

  let projectedBase = Number(order?.projectedMeters ?? 0) || 0
  if (!projectedBase) {
    const knifeCode = order?.selectedProduct?.knifeCode
    const knife = await getKnife(knifeCode)
    projectedBase = calcProjectedMeters(order, knife)
  }

  const projectedTotal = projectedBase + MACHINE_STARTUP_METERS
  if (!(Number(projectedTotal) || Number(used))) {
    return { kind: "skip", reason: "no-data" }
  }

  const deltaM = projectedTotal - used
  const deltaM2 = calcDeltaM2(order, deltaM)

  const materialId = order?.selectedProduct?.materialId
  const material = await getMaterial(materialId)
  const eurM2 = Number(material?.eurM2) || 0
  const costDelta = Math.abs(deltaM2) * eurM2

  return {
    kind: "ok",
    MACHINE_STARTUP_METERS,
    projectedBase,
    projectedTotal,
    used,
    deltaM,
    deltaM2,
    costDelta,
    eurM2,
  }
}
